/**
 * OnboardingTour — leggero tour guidato a 3 step per il Portale Dipendente.
 *
 * Caratteristiche:
 *  - Spotlight sull'elemento target (box-shadow gigantesco per oscurare il resto)
 *  - Tooltip posizionato automaticamente sopra/sotto in base allo spazio disponibile
 *  - Bottoni: Indietro, Avanti, Salta tour (skip)
 *  - Step opzionali (se il target non esiste, viene saltato automaticamente)
 *  - Si chiude con ESC, click sul backdrop o "Fine"
 *  - Persistenza: imposta `localStorage[storageKey] = "1"` al completamento o skip
 *
 * Props:
 *  - steps: Array<{ target: string (selector CSS), title: string, body: ReactNode, placement?: "top"|"bottom"|"auto" }>
 *  - storageKey: string — chiave localStorage per disabilitare i tour successivi
 *  - autoStart: boolean — se true e flag non presente, avvia automaticamente
 *  - delayMs: numero ms da attendere prima di mostrare il primo step (default 700)
 *  - onComplete?: callback
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, ArrowRight, X } from "lucide-react";

const PAD = 8; // padding intorno all'elemento per lo spotlight
const TOOLTIP_MARGIN = 12;
const TOOLTIP_W = 320;

function findFirstVisible(selector) {
    if (!selector) return null;
    const all = document.querySelectorAll(selector);
    for (const el of all) {
        const rect = el.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) return el;
    }
    return null;
}

export default function OnboardingTour({
    steps,
    storageKey = "ss_onboarding_done_v1",
    autoStart = true,
    delayMs = 700,
    onComplete,
}) {
    const [open, setOpen] = useState(false);
    const [idx, setIdx] = useState(0);
    const [rect, setRect] = useState(null);
    const startedRef = useRef(false);

    // Compute the list of steps whose target actually exists at start time.
    const computeAvailableSteps = useCallback(() => {
        return steps
            .map((s, i) => ({ ...s, _i: i, _el: findFirstVisible(s.target) }))
            .filter((s) => s._el);
    }, [steps]);

    const [available, setAvailable] = useState([]);

    // Autostart
    useEffect(() => {
        if (!autoStart || startedRef.current) return;
        let done = false;
        try {
            done = localStorage.getItem(storageKey) === "1";
        } catch (e) {
            done = false;
        }
        if (done) return;
        startedRef.current = true;

        // Wait until any blocking Radix/shadcn Dialog has closed before starting the tour.
        const hasOpenModal = () =>
            document.querySelectorAll('[role="dialog"][data-state="open"]').length > 0;

        let cancelled = false;
        let attempts = 0;
        const MAX_ATTEMPTS = 30; // ~18 s if any modal stays open this long, give up.

        const tryStart = () => {
            if (cancelled) return;
            if (hasOpenModal() && attempts < MAX_ATTEMPTS) {
                attempts += 1;
                window.setTimeout(tryStart, 600);
                return;
            }
            const avail = computeAvailableSteps();
            if (avail.length === 0) {
                try { localStorage.setItem(storageKey, "1"); } catch (e) { /* noop */ }
                return;
            }
            setAvailable(avail);
            setIdx(0);
            setOpen(true);
        };

        const t = setTimeout(tryStart, delayMs);
        return () => {
            cancelled = true;
            clearTimeout(t);
        };
    }, [autoStart, storageKey, delayMs, computeAvailableSteps]);

    const current = available[idx];

    // Track target rect (re-measure on scroll/resize)
    useLayoutEffect(() => {
        if (!open || !current?._el) return;
        const update = () => {
            const r = current._el.getBoundingClientRect();
            setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        };
        update();
        window.addEventListener("resize", update);
        window.addEventListener("scroll", update, true);
        // Scroll the target into view (smoothly) for steps below the fold.
        try {
            current._el.scrollIntoView({ behavior: "smooth", block: "center" });
            // Re-measure after a moment in case scrollIntoView moved it.
            setTimeout(update, 350);
        } catch (e) { /* noop */ }
        return () => {
            window.removeEventListener("resize", update);
            window.removeEventListener("scroll", update, true);
        };
    }, [open, current]);

    const finish = useCallback(() => {
        try { localStorage.setItem(storageKey, "1"); } catch (e) { /* noop */ }
        setOpen(false);
        if (typeof onComplete === "function") onComplete();
    }, [storageKey, onComplete]);

    const next = useCallback(() => {
        setIdx((i) => {
            if (i + 1 >= available.length) {
                // last step -> finish
                setTimeout(finish, 0);
                return i;
            }
            return i + 1;
        });
    }, [available.length, finish]);

    const prev = useCallback(() => setIdx((i) => Math.max(0, i - 1)), []);

    // ESC closes the tour
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") finish();
            if (e.key === "ArrowRight") next();
            if (e.key === "ArrowLeft") prev();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, finish, next, prev]);

    const tooltipPos = useMemo(() => {
        if (!rect) return null;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const targetCenterX = rect.left + rect.width / 2;
        const spaceBelow = vh - (rect.top + rect.height);
        const spaceAbove = rect.top;
        const placement =
            current?.placement === "top"
                ? "top"
                : current?.placement === "bottom"
                  ? "bottom"
                  : spaceBelow >= 200 || spaceBelow >= spaceAbove
                    ? "bottom"
                    : "top";
        const top =
            placement === "bottom"
                ? rect.top + rect.height + TOOLTIP_MARGIN
                : rect.top - TOOLTIP_MARGIN; // tooltip's bottom anchors here
        // Horizontal: try to center on target, but clamp inside the viewport.
        let left = targetCenterX - TOOLTIP_W / 2;
        const minLeft = 12;
        const maxLeft = vw - TOOLTIP_W - 12;
        if (left < minLeft) left = minLeft;
        if (left > maxLeft) left = Math.max(minLeft, maxLeft);
        return { top, left, placement };
    }, [rect, current]);

    if (!open || !current || !rect || !tooltipPos) return null;

    const overlayStyle = {
        position: "fixed",
        top: rect.top - PAD,
        left: rect.left - PAD,
        width: rect.width + PAD * 2,
        height: rect.height + PAD * 2,
        borderRadius: 10,
        boxShadow: "0 0 0 9999px rgba(15, 23, 42, 0.62)",
        pointerEvents: "none",
        transition: "all 220ms ease",
        zIndex: 10000,
    };

    const tooltipStyle = {
        position: "fixed",
        top: tooltipPos.placement === "top" ? "auto" : tooltipPos.top,
        bottom: tooltipPos.placement === "top" ? window.innerHeight - tooltipPos.top : "auto",
        left: tooltipPos.left,
        width: TOOLTIP_W,
        maxWidth: "calc(100vw - 24px)",
        zIndex: 10001,
    };

    return createPortal(
        <>
            {/* Backdrop to catch clicks (closes tour) — sits below the spotlight */}
            <div
                onClick={finish}
                style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 9998,
                    background: "transparent",
                    cursor: "pointer",
                }}
                data-testid="onboarding-backdrop"
            />
            {/* Spotlight (purely visual) */}
            <div style={overlayStyle} data-testid="onboarding-spotlight" />
            {/* Tooltip */}
            <div
                role="dialog"
                aria-label={current.title}
                style={tooltipStyle}
                className="rounded-xl border border-slate-200 bg-white p-4 shadow-2xl"
                data-testid="onboarding-tooltip"
            >
                <div className="mb-2 flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-[#376DB3] px-2 text-xs font-bold text-white">
                            {idx + 1}/{available.length}
                        </span>
                        <h3 className="font-display text-base font-bold text-[#376DB3]">
                            {current.title}
                        </h3>
                    </div>
                    <button
                        type="button"
                        onClick={finish}
                        className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        title="Salta il tour"
                        data-testid="onboarding-skip"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div className="text-sm leading-relaxed text-slate-600">
                    {current.body}
                </div>
                <div className="mt-4 flex items-center justify-between gap-2">
                    <button
                        type="button"
                        onClick={finish}
                        className="text-xs font-semibold text-slate-500 hover:text-slate-700 hover:underline"
                        data-testid="onboarding-skip-link"
                    >
                        Salta tour
                    </button>
                    <div className="flex items-center gap-2">
                        {idx > 0 && (
                            <button
                                type="button"
                                onClick={prev}
                                className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                data-testid="onboarding-prev"
                            >
                                <ArrowLeft className="h-3.5 w-3.5" />
                                Indietro
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={next}
                            className="inline-flex items-center gap-1 rounded-md bg-[#376DB3] px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-[#2c5994]"
                            data-testid="onboarding-next"
                        >
                            {idx + 1 >= available.length ? "Fine" : "Avanti"}
                            {idx + 1 < available.length && <ArrowRight className="h-3.5 w-3.5" />}
                        </button>
                    </div>
                </div>
            </div>
        </>,
        document.body,
    );
}
